import { callBackend } from '../../utils/api';
import { holdingsSourceMatches } from '../historicalPerformanceBasis';
import {
    EXPLORE_CONTRACT, normalizeExploreEvent, normalizeExploreEvents, normalizeExploreMarkets, normalizeStressLibrary,
} from './exploreColumns';
import { ENVIRONMENT_FEATURE_VERSION } from './exploreEnvironment';

const NOT_READY = 'The Compare & Explore service is not ready. Refresh after the application update completes.';
const conflict = (message) => Object.assign(new Error(message), { code: 'context_changed' });

// The current published stress library: its version (null when none is
// published) and its visible episodes.
export async function fetchStressLibrary({ signal } = {}) {
    const library = normalizeStressLibrary(await callBackend('api/stress-library', 'GET', null, { signal }));
    if (!library) throw new Error('The stress library is unavailable.');
    return library;
}

const EVENTS = 'api/performance/explore/events';

// Your events and how many you can keep.
export async function fetchExploreEvents({ signal } = {}) {
    const events = normalizeExploreEvents(await callBackend(EVENTS, 'GET', null, { signal }));
    if (!events) throw new Error(NOT_READY);
    return events;
}

function savedEvent(response) {
    const event = normalizeExploreEvent(response?.event);
    if (!event) throw new Error(NOT_READY);
    return event;
}

// `draft` is { title, start_date, end_date, notes }. A taken title or a full
// set of events answers 409 with a message that says so.
export async function createExploreEvent(draft) {
    return savedEvent(await callBackend(EVENTS, 'POST', draft));
}

export async function updateExploreEvent(eventId, draft) {
    return savedEvent(await callBackend(`${EVENTS}/${eventId}`, 'PUT', draft));
}

export async function deleteExploreEvent(eventId) {
    await callBackend(`${EVENTS}/${eventId}`, 'DELETE');
}

// The markets a comparison can show beside its portfolios: actual ETF returns.
export async function fetchExploreMarkets({ signal } = {}) {
    const markets = normalizeExploreMarkets(await callBackend('api/performance/explore/markets', 'GET', null, { signal }));
    if (!markets) throw new Error(NOT_READY);
    return markets;
}

// Totals, ranges and coverage for each portfolio and period; rows come back
// in request order, one per requested portfolio, and market rows one per
// requested market.
export async function fetchExploreGrid(request, { signal } = {}) {
    const response = await callBackend('api/performance/explore/grid', 'POST', request, { signal });
    const markets = request.markets || [];
    const marketRows = response?.market_rows ?? [];
    if (response?.success !== true || response.calculation_contract !== EXPLORE_CONTRACT
        || !Array.isArray(response.columns) || !Array.isArray(response.rows)
        || response.rows.length !== request.portfolios.length
        || !Array.isArray(marketRows) || marketRows.length !== markets.length
        || marketRows.some((row, index) => row?.market !== markets[index])) throw new Error(NOT_READY);
    response.rows.forEach((row, index) => {
        if (!holdingsSourceMatches(row?.constant_source, request.portfolios[index].constant_portfolio_source)) {
            throw conflict('The compared portfolios changed. Refresh the grid.');
        }
    });
    return response;
}

// One cell's factor and stock contributions and its daily path. The grid
// row's digests make the numbers match the cell, or the route answers 409.
export async function fetchExploreDetail(request, { signal } = {}) {
    const response = await callBackend('api/performance/explore/detail', 'POST', request, { signal });
    if (response?.success !== true || response.calculation_contract !== EXPLORE_CONTRACT || !response.result
        || !Array.isArray(response.by_factor) || !Array.isArray(response.daily_path)) throw new Error(NOT_READY);
    if (!holdingsSourceMatches(response.portfolio?.constant_source, request.constant_portfolio_source)) {
        throw conflict('The selected portfolio changed. Refresh the grid.');
    }
    return response;
}

// Today's factor environment for the user's factor release: the resemblance
// to past stress lead-ins and a score per month. The portfolio (a grid row's
// source and context digest) fences it as in the grid.
export async function fetchExploreEnvironment(request, { signal } = {}) {
    const response = await callBackend('api/performance/explore/environment', 'POST', request, { signal });
    if (response?.success !== true || response.feature_version !== ENVIRONMENT_FEATURE_VERSION
        || !Array.isArray(response.months) || !response.resemblance || typeof response.resemblance !== 'object') throw new Error(NOT_READY);
    return response;
}

// Published market-calendar entries overlapping a window. Before the
// calendar route is deployed it answers 404, which reads as no calendar yet.
export async function fetchMarketCalendar(start, end, { signal } = {}) {
    const query = new URLSearchParams({ from: start, to: end }).toString();
    try {
        const response = await callBackend(`api/market-calendar?${query}`, 'GET', null, { signal });
        if (!Array.isArray(response?.entries)) throw new Error('The market calendar is unavailable.');
        return { available: true, entries: response.entries };
    } catch (error) {
        if (error?.status === 404) return { available: false, entries: [] };
        throw error;
    }
}
